import { useQuery, useMutation } from "@tanstack/react-query";
import type { Texture, Tag, Category } from "@textureninja/shared";
import { apiGet, apiPost } from "./api-client.js";

export const queryKeys = {
  categories: ["categories"] as const,
  latestTextures: ["latestTextures"] as const,
  textures: (params: Record<string, unknown>) => ["textures", params] as const,
  textureByKey: (key: string) => ["textureByKey", key] as const,
  tags: (filter: string, categoryIds: number[] | undefined) =>
    ["tags", filter, categoryIds ? [...categoryIds].sort() : null] as const,
};

export function useCategories() {
  return useQuery({
    queryKey: queryKeys.categories,
    queryFn: () => apiGet<Category[]>("/api/categories"),
    staleTime: 30 * 60 * 1000,
  });
}

export function useLatestTextures() {
  return useQuery({
    queryKey: queryKeys.latestTextures,
    queryFn: () => apiGet<Texture[]>("/api/textures/latest"),
    staleTime: 5 * 60 * 1000,
  });
}

interface TextureFilters {
  category_id?: number[];
  tag?: string;
  tags?: string[];
}

export function useTextures(filters: TextureFilters, enabled = true) {
  return useQuery({
    queryKey: queryKeys.textures(filters as unknown as Record<string, unknown>),
    queryFn: () =>
      apiGet<Texture[]>("/api/textures", filters as unknown as Record<string, unknown>),
    enabled,
  });
}

export function useTextureByKey(key: string | null) {
  return useQuery({
    queryKey: queryKeys.textureByKey(key ?? ""),
    queryFn: () => apiGet<Texture[]>("/api/textures", { textureKey: key }),
    enabled: !!key,
    select: (rows) => rows[0],
  });
}

export function useTagSearch(filter: string) {
  return useQuery({
    queryKey: queryKeys.tags(filter, undefined),
    queryFn: () => apiGet<Tag[]>("/api/tags", { filter }),
    staleTime: 60_000,
  });
}

export function useCategoryTags(categoryIds: number[] | null) {
  return useQuery({
    queryKey: queryKeys.tags("", categoryIds ?? undefined),
    queryFn: () => apiGet<Tag[]>("/api/tags", { category_ids: categoryIds }),
    enabled: !!categoryIds && categoryIds.length > 0,
  });
}

export function useRecordDownload() {
  return useMutation({
    mutationFn: (id: number) => apiPost<void>(`/api/textures/${id}/download`),
  });
}
